import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@arcadeai/design-system/components/ui/atoms/dialog";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import { Textarea } from "@arcadeai/design-system/components/ui/atoms/textarea";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@arcadeai/design-system/rhf";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { useCreateProject } from "@/app/projects/api/hooks";
import { useOrgProjectRouteContext } from "@/lib/org-project-context/hooks";
import { type CreateProjectFormData, CreateProjectSchema } from "../types";

export const CreateProjectModal = ({
  setIsOpen,
}: {
  setIsOpen: (isOpen: boolean) => void;
}) => {
  const { organizationId } = useOrgProjectRouteContext();
  const { isPending: isCreating, mutateAsync: createProject } =
    useCreateProject(organizationId);
  const form = useForm<CreateProjectFormData>({
    resolver: zodResolver(CreateProjectSchema),
    defaultValues: {
      name: "",
      description: "",
    },
  });

  const onSubmit = async (data: CreateProjectFormData) => {
    await createProject({
      name: data.name,
      description: data.description,
    });
    form.reset();
    setIsOpen(false);
  };

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Create New Project</DialogTitle>
      </DialogHeader>
      <DialogDescription>
        Projects help you organize your tools, workers, OAuth providers, secrets
        and API keys
      </DialogDescription>
      <Form {...form}>
        <form className="mt-4 space-y-5" onSubmit={form.handleSubmit(onSubmit)}>
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Project Name</FormLabel>
                <FormControl>
                  <Input
                    placeholder="My Awesome Project"
                    {...field}
                    autoFocus
                    disabled={isCreating}
                  />
                </FormControl>
                <FormDescription>
                  Choose a unique name for your project
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description (Optional)</FormLabel>
                <FormControl>
                  <Textarea
                    className="resize-none"
                    placeholder="Describe what this project is for..."
                    rows={3}
                    {...field}
                    disabled={isCreating}
                  />
                </FormControl>
                <FormDescription>
                  A brief description to help team members understand the
                  project
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <DialogFooter className="flex justify-end gap-4">
            <Button disabled={isCreating} type="submit">
              {!!isCreating && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Create Project
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </DialogContent>
  );
};
